import { InfoButton } from '../../components/connectedForm/Button';
import hasValidationErrors from '../../components/connectedForm/utils/hasValidationErrors';
import { createBackgroundProcess } from '../../utils/BackgroundJobs';
import PurchaseAllocationAutoAllocateMutation from 'graphql/mutations/supplier/PurchaseAllocationAutoAllocateMutation.graphql';
import PurchaseAllocationReverseAllocationsMutation from 'graphql/mutations/supplier/PurchaseAllocationReverseAllocationsMutation.graphql';
import PurchaseAllocationPostMutation from 'graphql/mutations/supplier/PurchaseAllocationPostMutation.graphql';
import updateStoreFieldValues from '../../components/connectedForm/utils/updateStoreFieldValues';
import { PostSelectButton } from '../../components/SelectButton';
import getClientMutationId from '../../utils/getClientMutationId';
import { IDeprecatedWorkspaceAction, IWorkspaceAction } from '../../@types/workspaceTypes';
import { IPurchaseAllocation } from '../../graphql/schema/p4types';
import { PurchaseAllocationAction } from '../../graphql/entities/supplier/PurchaseAllocationAuditReport';
import { useMemo } from 'react';

export const formActionsForPermissions = [
  { description: 'Auto Allocate' },
  { description: 'Post Allocations' },
  { description: 'Reverse Allocations' },
  { actionReference: PurchaseAllocationAction.AllocationAuditReport },
];

export const usePurchaseAllocationActions = () => {
  return useMemo(() => {
    const actions: Array<IWorkspaceAction | IDeprecatedWorkspaceAction> = [
      {
        key: 'SUPPLIER_ALLOCATIONS_POST ALLOCATIONS',
        description: 'Post Allocations',
        actionReference: 'SUPPLIER_ALLOCATIONS_POST ALLOCATIONS',
        screenReference: 'SUPPLIER_ALLOCATIONS_POST ALLOCATIONS',
        component: PostSelectButton,
        fn: postAllocate,
        individualButton: true,
        performSave: true,
        saveMessage: 'Transactions allocated',
        condition: canBePosted,
      },
      {
        key: 'SUPPLIER_ALLOCATIONS_AUTO ALLOCATE',
        description: 'Auto Allocate',
        actionReference: 'SUPPLIER_ALLOCATIONS_AUTO ALLOCATE',
        screenReference: 'SUPPLIER_ALLOCATIONS_AUTO ALLOCATE',
        component: InfoButton,
        fn: autoAllocate,
        individualButton: true,
        performSave: true,
        saveMessage: 'Transactions auto allocated',
        condition: canBePosted,
      },
      {
        key: 'SUPPLIER_ALLOCATIONS_REVERSE ALLOCATIONS',
        description: 'Reverse Allocations',
        actionReference: 'SUPPLIER_ALLOCATIONS_REVERSE ALLOCATIONS',
        screenReference: 'SUPPLIER_ALLOCATIONS_REVERSE ALLOCATIONS',
        component: InfoButton,
        fn: reverseAllocations,
        individualButton: true,
        performSave: true,
        saveMessage: 'Allocations reversed',
        condition: canBeReversed,
      },
    ];

    return [actions];
  }, []);
};

const canBePosted: (allocation: IPurchaseAllocation) => boolean = (allocation) => {
  if (allocation && allocation.allocationOnly === false) return false;
  if (allocation.supplier === null) return false;
  if (allocation && allocation.documentStatus) {
    return allocation.documentStatus === 'DRAFT';
  }
  return false;
};

const canBeReversed: (allocation: IPurchaseAllocation) => boolean = (allocation) => {
  return (
    !!(allocation && allocation.documentStatus && allocation.documentStatus === 'POSTED') && !allocation.foreignCurrency
  );
};

const autoAllocate: (props: any) => void = (props) => {
  const { values, connectedForm } = props;
  if (hasValidationErrors(connectedForm)) return;
  const input = {
    clientMutationId: getClientMutationId(),
    id: values.id,
  };
  createBackgroundProcess(
    PurchaseAllocationAutoAllocateMutation,
    input,
    props,
    null,
    'Auto Allocate',
    updateStoreFieldValues
  );
};

const reverseAllocations: (props: any) => void = (props) => {
  const { values } = props;
  const input = {
    clientMutationId: getClientMutationId(),
    id: values.id,
  };
  createBackgroundProcess(PurchaseAllocationReverseAllocationsMutation, input, props, 'back', 'Reverse Allocations');
};

const postAllocate: (props: any) => void = (props) => {
  const { values, connectedForm } = props;
  if (hasValidationErrors(connectedForm)) return;
  const input = {
    clientMutationId: getClientMutationId(),
    id: values.id,
  };
  createBackgroundProcess(PurchaseAllocationPostMutation, input, props, 'back', 'Post Allocations');
};
